import React, { useEffect, useState } from 'react';

import {
  Checkbox,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import ContentCard from '../../components/ContentCard';
import PageContent from '../../components/PageContent';
import WeekCalendar from '../../components/WeekCalendar';
import { CalendarEntry } from '../../components/WeekCalendar/types';
import { useGetLessonsQuery } from '../../services/classes';
import { Lesson } from '../../services/classes/types';
import { useGetStaffsQuery } from '../../services/staff';
import { Staff } from '../../services/staff/types';
import { UserGroup } from '../../types/common';
import addDays from '../../utils/common/addDays';
import { getLessonClassName } from '../../utils/common/classes';
import useSnackBar from '../../utils/react/hooks/useSnackbar';

import { LoadingWrapper } from './index.styles';
import LessonEntry from './LessonEntry';
import { TimesheetLessonDetails } from './types';

const TODAY = dayjs().startOf('day');

const AdminTimesheetPage = () => {
  const createSnackbar = useSnackBar();

  // Earliest date to fetch lessons
  const [startDate, setStartDate] = useState<Dayjs>(addDays(TODAY, -14));

  // Latest date to fetch lessons
  const [endDate, setEndDate] = useState<Dayjs>(addDays(TODAY, 14));

  // Controls
  const [includeUntimesheeted, setIncludeUntimesheeted] = useState(false);
  const [includeTeacherless, setIncludeTeacherless] = useState(false);

  // Fetch teachers
  const { data: teachers, isLoading: isStaffsLoading } = useGetStaffsQuery({
    group: UserGroup.Teachers,
  });

  // Fetch lessons
  const { data: lessons, isLoading: isLessonsLoading } = useGetLessonsQuery({
    queryStartDatetime: startDate.valueOf(),
    queryEndDatetime: endDate.valueOf(),
  });
  const [lessonEvents, setLessonEvents] = useState<CalendarEntry<TimesheetLessonDetails>[]>();

  // Copy text
  const copyToClipboard = (text: string) => {
    // Copy string to clipboard
    navigator.clipboard.writeText(text);
    createSnackbar('Class name copied to clipboard', 'success');
  };

  const toCalendarEntry = (lesson: Lesson & { teacherName: string }):
  CalendarEntry<TimesheetLessonDetails> => ({
    id: `${lesson.startDatetime}#${lesson.subjectClassIdentifier}#${lesson.lesson}`,
    startDatetime: lesson.startDatetime,
    details: {
      ...lesson,
      shortTeacherName: `${lesson.teacherName.split(' ')[0]} ${lesson.teacherName.split(' ').slice(1).map((name) => `${name[0]}.`).join(' ')}`,
    },
  });

  useEffect(() => {
    if (lessons && teachers) {
      const sortedLessonsData = [...lessons.data]
        .filter((lesson: Lesson) => includeTeacherless || lesson.teacher)
        .map((lesson: Lesson) => ({
          ...lesson,
          teacherName: teachers?.data.find((t: Staff) => t.staffId === lesson.teacher)?.name || '???',
        }))
        .sort((a, b) => {
          // Sort by startDatetime first, then by teacher name
          if (a.startDatetime === b.startDatetime) {
            return a.teacherName.localeCompare(b.teacherName);
          }
          return a.startDatetime - b.startDatetime;
        });

      setLessonEvents(sortedLessonsData.map((lesson) => toCalendarEntry(lesson)));
    }
  }, [lessons, teachers, includeTeacherless]);

  return (
    <PageContent>
      {(isLessonsLoading || isStaffsLoading || !lessonEvents) ? (
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      ) : (
        <ContentCard title="Teacher Timesheet" variant="outlined">
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
              >
                <Checkbox
                  checked={includeUntimesheeted}
                  onChange={() => setIncludeUntimesheeted((prev) => !prev)}
                />
                <Typography
                  variant="body2"
                >
                  Include non-timesheeted lessons
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
              >
                <Checkbox
                  checked={includeTeacherless}
                  onChange={() => setIncludeTeacherless((prev) => !prev)}
                />
                <Typography
                  variant="body2"
                >
                  Include lessons with unknown teacher
                </Typography>
              </Stack>
            </Stack>
            <WeekCalendar
              data={lessonEvents}
              renderContent={(entry) => entry.details && (
                <LessonEntry
                  key={entry.id}
                  startDatetime={entry.startDatetime}
                  lesson={entry.details}
                  onClick={() => copyToClipboard(getLessonClassName(entry.details))}
                  renderTimesheetedOnly={!includeUntimesheeted}
                />
              )}
              minDate={startDate}
              maxDate={endDate}
              setMinDate={setStartDate}
              setMaxDate={setEndDate}
            />
          </Stack>
        </ContentCard>
      )}
    </PageContent>
  );
};
export default AdminTimesheetPage;
